import { trpc } from "@/Utils/trpc";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

import { useClose } from "@headlessui/react";
import type { Terminal } from "server/dwhApi/terminals/types.js";
import { twMerge } from "tailwind-merge";

import { ALL_BUILDINGS_VIEW_KEY } from "../constants";
import type { SavedBuildingView } from "../types";

type Props = {
  currentViewId?: string;
  selectedBuildingIds: string[] | null;
  terminals: Terminal[];
  applyView: (view: SavedBuildingView | null) => void;
  allViews: SavedBuildingView[];
};

export const PopupFooter: React.FC<Props> = ({
  currentViewId,
  selectedBuildingIds,
  terminals,
  applyView,
  allViews,
}) => {
  const { t } = useTrans();
  const close = useClose();
  const trpcUtils = trpc.useUtils();
  const [newViewName, setNewViewName] = useState("");

  const { mutate: createBuildingSelection, isPending: isCreatingView } =
    trpc.config.createBuildingSelection.useMutation({
      onSuccess: () => {
        trpcUtils.config.getBuildingSelections.invalidate();
        setNewViewName("");
      },
    });

  const isUnsavedSelection = useMemo(() => !currentViewId, [currentViewId]);

  const selectedCount = useMemo(
    () => (selectedBuildingIds ? selectedBuildingIds.length : terminals.length),
    [selectedBuildingIds, terminals.length],
  );

  const allIds = useMemo(
    () => terminals.map((term) => term.terminalId),
    [terminals],
  );

  const allBuildingsView = useMemo(
    () => allViews.find((v) => v.id === ALL_BUILDINGS_VIEW_KEY),
    [allViews],
  );

  const saveNewView = useCallback(() => {
    const name = newViewName.trim();
    if (!name || isCreatingView || allViews.some((v) => v.name === name)) {
      return;
    }

    createBuildingSelection({
      name,
      terminalIds: selectedBuildingIds ?? allIds,
    });
  }, [
    allIds,
    allViews,
    createBuildingSelection,
    isCreatingView,
    newViewName,
    selectedBuildingIds,
  ]);

  return isUnsavedSelection ? (
    <div className="border-grey2 border-t bg-[#FBFBE9] p-[10px]">
      <div className="text-grey6 mb-[6px] text-[12px]">
        {t("portfolioPage:buildingSelectionUnsavedViewText", { selectedCount })}
      </div>
      <div className="flex gap-[6px]">
        <input
          value={newViewName}
          placeholder={t(
            "portfolioPage:buildingSelectionNameSelectionPlaceholder",
          )}
          onChange={(e) => setNewViewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveNewView();
          }}
          className="border-grey3 min-w-0 flex-1 rounded-md border px-[10px] py-2 text-[13px] outline-none"
        />
        <button
          onClick={saveNewView}
          disabled={!newViewName.trim() || isCreatingView}
          className={twMerge(
            "rounded-md px-[13px] py-2 text-[13px] font-semibold whitespace-nowrap",
            newViewName.trim() && !isCreatingView
              ? "bg-lime cursor-pointer"
              : "bg-grey2 text-grey4",
          )}
        >
          {t(
            `portfolioPage:${isCreatingView ? "buildingSelectionSaving" : "buildingSelectionSaveView"}`,
          )}
        </button>
      </div>
    </div>
  ) : (
    <div className="border-grey2 flex items-center justify-between gap-2 border-t p-[10px]">
      <button
        onClick={() => applyView(allBuildingsView || null)}
        className="text-grey6 cursor-pointer text-[12px] underline"
      >
        {t("portfolioPage:buildingSelectionResetAll")}
      </button>
      <button
        onClick={close}
        className="bg-lime cursor-pointer rounded-md px-4 py-2 text-[13px] font-semibold"
      >
        {t("portfolioPage:buildingSelectionDoneButton")}
      </button>
    </div>
  );
};
